// Splits a date range into the part whole ISO weeks cover and the ragged days
// left over. The rollups are only ever read for whole Mon-Sun weeks; everything
// else comes from scheduling_fact.
//
// The two parts are DISJOINT BY DAY, which is what makes combining their totals
// safe:
//   - a day is either inside a fully-covered week or it is not
//   - therefore no (doctor, date) pair appears in both, so dvmDays and
//     distinctDates can be added rather than deduplicated
//   - and an ISO week is either fully covered or not in the rollup at all, so
//     dvmWeeks can be added too
//
// distinctDvms and siteCount still need set UNIONS: the same doctor or site can
// appear in both halves. Adding those would double-count.

import { DateTime } from "luxon";
import type { DateRange, PeriodFamily } from "./periods";
import type { Granularity } from "../types/filters";

export interface RangeSplit {
  /** Mondays of the ISO weeks entirely inside the range. Empty when none are. */
  wholeWeeks: string[];
  /** Days not covered by any whole week — the ragged ends. */
  edgeDays: string[];
  /** Convenience: the contiguous spans of edgeDays, for a BETWEEN query. */
  edgeSpans: Array<[string, string]>;
}

// A week-grain rollup row can only be used when the whole week lands in ONE
// bucket. getBucketKey decides that, and the two families differ:
//
//   trailing + month  the bucket is the month holding the week's THURSDAY, so a
//                     week is never split -- every whole week is usable
//   calendar + month  the bucket is each DATE's own calendar month, so a week
//                     straddling a month boundary belongs to two buckets at once
//                     and cannot come from one rollup row
//
// Only YTD offers calendar + month, and only ~7 of its weeks straddle a boundary,
// so those fall back to raw days. Cheaper than a month-grain rollup table and a
// 31-bit mask, which is what avoiding it would cost.
function weekFitsOneBucket(
  monday: DateTime,
  granularity: Granularity,
  family: PeriodFamily
): boolean {
  if (granularity !== "month" || family !== "calendar") return true;
  return monday.month === monday.plus({ days: 6 }).month;
}

export function splitRange(
  range: DateRange,
  granularity: Granularity = "week",
  family: PeriodFamily = "trailing"
): RangeSplit {
  const start = DateTime.fromISO(range.startDate);
  const end = DateTime.fromISO(range.endDate);
  if (!start.isValid || !end.isValid || end < start) {
    return { wholeWeeks: [], edgeDays: [], edgeSpans: [] };
  }

  // Day granularity never uses the rollups: a daily bucket needs daily numbers,
  // and the trend granularity cap bounds those ranges at ~35 days.
  if (granularity === "day") {
    const edgeDays: string[] = [];
    for (let d = start; d <= end; d = d.plus({ days: 1 })) edgeDays.push(d.toISODate()!);
    return { wholeWeeks: [], edgeDays, edgeSpans: [[range.startDate, range.endDate]] };
  }

  const wholeWeeks: string[] = [];
  const covered = new Set<string>();

  // Walk Mondays from the first one on or after start.
  let monday = start.startOf("week");
  if (monday < start) monday = monday.plus({ weeks: 1 });
  while (monday.plus({ days: 6 }) <= end) {
    if (weekFitsOneBucket(monday, granularity, family)) {
      wholeWeeks.push(monday.toISODate()!);
      for (let i = 0; i < 7; i++) covered.add(monday.plus({ days: i }).toISODate()!);
    }
    monday = monday.plus({ weeks: 1 });
  }

  const edgeDays: string[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const iso = d.toISODate()!;
    if (!covered.has(iso)) edgeDays.push(iso);
  }

  const edgeSpans: Array<[string, string]> = [];
  for (const day of edgeDays) {
    const last = edgeSpans[edgeSpans.length - 1];
    if (last && DateTime.fromISO(last[1]).plus({ days: 1 }).toISODate() === day) {
      last[1] = day;
    } else {
      edgeSpans.push([day, day]);
    }
  }

  return { wholeWeeks, edgeDays, edgeSpans };
}
